/**
 * errorTranslator — anti-enumeration + safe-to-surface error classification.
 *
 * Classifies raw backend errors into generic user-facing messages per
 * OWASP ASVS 2.2.2 + NIST 800-63B. The original error is preserved on
 * `err.cause` for audit-log consumers; the UI only ever sees the generic.
 *
 * Classification rules:
 *   - 401 / 403 / 404 → credentials class. Message is `GENERIC_LOGIN_ERROR`
 *     on login operation; `GENERIC_MFA_ERROR` on mfa operation. 404 gets the
 *     credentials class specifically to block email-enumeration oracles.
 *   - 429             → rate_limit class → `GENERIC_RATE_LIMIT_ERROR`.
 *   - 5xx             → server class → `GENERIC_SERVER_ERROR`.
 *   - anything else (missing status, TypeError, AbortError, null, etc.)
 *                     → network class → `GENERIC_NETWORK_ERROR`.
 *
 * @module @puffin-core/login-shell/utils/errorTranslator
 */

import { FACTOR_SETUP_REQUIRED, factorOfSetupRequired } from '@puffin-core/factor-enrollment-codes';
import { DEFAULT_LOGIN_STRINGS } from '../strings/en.js';

// LOGIN-CONVERGENCE W7 (2026-10-11): every sentence this translator assigns is a leaf of the
// package's own `login` catalog, and an `AuthError` carries that leaf's KEY (`messageKey`), so a form
// renders it in the reader's language. The constants below are the ENGLISH leaves, read from the
// catalog rather than typed twice, for the consumers that store `err.message` (the auth contexts).
export const GENERIC_LOGIN_ERROR = DEFAULT_LOGIN_STRINGS['login.error.credentials'];
export const GENERIC_MFA_ERROR = DEFAULT_LOGIN_STRINGS['login.error.mfa'];
export const GENERIC_SERVER_ERROR = DEFAULT_LOGIN_STRINGS['login.error.server'];
export const GENERIC_NETWORK_ERROR = DEFAULT_LOGIN_STRINGS['login.error.network'];
export const GENERIC_RATE_LIMIT_ERROR = DEFAULT_LOGIN_STRINGS['login.error.rateLimit'];
// AUTH-LOGIN-FIDELITY-001: a 400 is a malformed/invalid request, NOT a
// credentials check — so it gets a neutral message (no anti-enumeration concern)
// rather than being misclassified as a transport "Network error."
export const GENERIC_BAD_REQUEST_ERROR = DEFAULT_LOGIN_STRINGS['login.error.badRequest'];

/**
 * N152 (MFA-SETUP-CONVERGE): the tenant sign-in's HOLD — the password was right, but the person must enrol a second
 * factor again before any session (owner ruling Q-ARG-1; `@puffin-core/auth-routes` `sendMfaEnrollmentRequired`, the
 * code `@puffin-core/auth-client` reads as `MFA_SETUP_REQUIRED_CODE`). Classed on its own (`mfa_enrollment`), never as
 * a wrong password: a context sign-in turns it into the enrolment screen, the marketing apex into the handoff to the
 * company's own sign-in page. Not an enumeration oracle — it is answered only after the password matched. The code is
 * the ONE vocabulary's (`@puffin-core/factor-enrollment-codes`, SECREQ S6).
 */
export const MFA_SETUP_REQUIRED_CODE = FACTOR_SETUP_REQUIRED.mfa;

/**
 * SECREQ S6: the hold's words for a passkey or a PIN the company MFA policy requires (the factor's own set-up step takes
 * the person on from here). Like the MFA hold, never a wrong password.
 */
const FACTOR_HOLD_KEYS = Object.freeze({
  passkey: 'login.error.passkeySetupRequired',
  pin: 'login.error.pinSetupRequired',
});

/**
 * D1 errorCode burn-down, family 2: the person proved who they are (a password, a passkey, a PIN or a second factor),
 * and their role may not sign in on this device (`device:login`, pos-api's `DEVICE_LOGIN_NOT_PERMITTED`, at the sign-in
 * and at the MFA verify alike). Said as such — never "check your credentials", which a right password made untrue.
 * Not an enumeration oracle: it is answered only to the proven person. Terminal: retrying the same sign-in cannot pass.
 */
export const DEVICE_LOGIN_NOT_PERMITTED_CODE = 'DEVICE_LOGIN_NOT_PERMITTED';

/**
 * The code every sign-in leg refuses with when the person's stores cannot be read (`@puffin-core/auth-routes`
 * `LOCATIONS_UNAVAILABLE`, 503; coordinator ruling 2026-10-19). Worded on its own — "we couldn't load your stores, try
 * again" — never as a generic server error, and never as a sign-in with no stores. Retryable on every step.
 */
export const LOCATIONS_UNAVAILABLE_CODE = 'LOCATIONS_UNAVAILABLE';
export const MFA_ENROLLMENT_REQUIRED_MESSAGE =
  DEFAULT_LOGIN_STRINGS['login.error.mfaEnrollmentRequired'];

/**
 * MFA-LOGIN-CONSOLIDATION-001 — curated, user-safe messages for the stable
 * verify error codes the shared `createMfaVerifyRouter` emits (`error.code`).
 * Unlike the anti-enumeration generic, these distinguish RETRYABLE (wrong code,
 * stay on the MFA step) from TERMINAL (challenge invalid / locked / session
 * expired / method not enrolled → return to sign-in), and a challenge store that could not answer (a 503, retryable:
 * stay on the step and try again, S13 A1). Only these seven codes are curated; ANY other
 * code (or none) falls through to `GENERIC_MFA_ERROR` with `terminal:false`.
 */
export const MFA_CODE_INVALID_MESSAGE = DEFAULT_LOGIN_STRINGS['login.error.mfaCodeInvalid'];
export const MFA_CHALLENGE_INVALID_MESSAGE =
  DEFAULT_LOGIN_STRINGS['login.error.mfaChallengeInvalid'];
export const MFA_LOCKED_MESSAGE = DEFAULT_LOGIN_STRINGS['login.error.mfaLocked'];
export const MFA_SESSION_EXPIRED_MESSAGE = DEFAULT_LOGIN_STRINGS['login.error.mfaSessionExpired'];
// ECOSYSTEM-EMAIL-001 Q-EE-35: a one-time code for a method the person did not enrol (400). Reached only from a
// sign-in whose method list predates the enrolled-only fix, so it is terminal: a fresh sign-in lists the right methods.
export const MFA_METHOD_NOT_ENROLLED_MESSAGE =
  DEFAULT_LOGIN_STRINGS['login.error.mfaMethodNotEnrolled'];
// D1 family 2: a method the verify route does not serve (400) — backup codes off, a one-time code on a platform verify,
// no passkey wired. Nothing was judged; a fresh sign-in lists the methods the route serves, so it is terminal.
export const MFA_METHOD_UNSUPPORTED_MESSAGE =
  DEFAULT_LOGIN_STRINGS['login.error.mfaMethodUnsupported'];

/**
 * Closed allowlist: curated verify code → { message, terminal }. Drift from the
 * backend `MFA_VERIFY_ERROR_CODES` SSOT is locked by the Phase-6 cross-app contract.
 */
const CURATED_MFA_ERRORS = Object.freeze({
  MFA_CODE_INVALID: { key: 'login.error.mfaCodeInvalid', terminal: false },
  MFA_CHALLENGE_INVALID: { key: 'login.error.mfaChallengeInvalid', terminal: true },
  MFA_LOCKED: { key: 'login.error.mfaLocked', terminal: true },
  MFA_SESSION_EXPIRED: { key: 'login.error.mfaSessionExpired', terminal: true },
  MFA_METHOD_NOT_ENROLLED: { key: 'login.error.mfaMethodNotEnrolled', terminal: true },
  MFA_METHOD_UNSUPPORTED: { key: 'login.error.mfaMethodUnsupported', terminal: true },
  // S13 A1: the challenge store could not answer (a 503). Nothing was checked, so the person stays on the step and
  // tries again; it is never worded as a wrong code.
  MFA_CHALLENGE_STORE_UNAVAILABLE: { key: 'login.error.mfaUnavailable', terminal: false },
});

/**
 * AuthError — thin Error subclass carrying a classification and the raw
 * original via `cause`. Consumers surface `.message` to the UI and
 * optionally forward the whole instance to audit logs.
 *
 * LOGIN-CONVERGENCE W7 (2026-10-11): it also carries `messageKey`, the `login`-namespace leaf whose
 * English text `.message` is, so a form renders it translated; and the server's own `errorCode` /
 * `errorDetails`, copied from the original, so a reader of the translated error can still tell a
 * named refusal (and the server's `retryAfter`) from an anonymous one (`describeSignInRefusal`).
 */
export class AuthError extends Error {
  constructor(
    message,
    {
      cause = null,
      errorClass = 'unknown',
      code = null,
      terminal = false,
      messageKey = null,
      errorCode = null,
      errorDetails = null,
    } = {},
  ) {
    super(message);
    this.name = 'AuthError';
    this.errorClass = errorClass;
    this.code = code;
    // MFA-LOGIN-CONSOLIDATION-001: true when the user cannot recover on the current
    // step and must return to sign-in (expired/locked/invalid challenge). Default false.
    this.terminal = terminal;
    this.cause = cause;
    this.messageKey = messageKey;
    this.errorCode = errorCode;
    this.errorDetails = errorDetails;
  }
}

/**
 * Build the translated error for one catalog leaf.
 *
 * @param {string} messageKey - The `login`-namespace leaf.
 * @param {unknown} err - The original.
 * @param {Object} fields - `errorClass`, `code`, and optionally `terminal`.
 * @returns {AuthError}
 */
function translated(messageKey, err, fields) {
  return new AuthError(DEFAULT_LOGIN_STRINGS[messageKey], {
    ...fields,
    cause: err instanceof Error ? err : null,
    messageKey,
    errorCode: extractBackendCode(err),
    errorDetails: err && typeof err === 'object' && err.errorDetails ? err.errorDetails : null,
  });
}

/**
 * The HTTP status, or null. ONE spelling — `err.status` — the fleet vocabulary every producer
 * on the auth path now speaks (`@puffin-core/auth-client` `authError`, api-client's normalized
 * error, this package's own fetch paths). AUTH-ERROR-VOCABULARY-001 (2026-08-28) deleted the
 * axios-shaped `response.status` arm: no auth producer attaches `.response` any more, and a
 * raw axios error reaching this translator would be a transport bug, not a shape to tolerate.
 * @param {unknown} err
 * @returns {number|null}
 */
function extractStatus(err) {
  if (err && typeof err === 'object' && typeof err.status === 'number') return err.status;
  return null;
}

/**
 * MFA-LOGIN-CONSOLIDATION-001: extract the backend curated error code across the
 * shapes the apiClient/axios layers surface it in. Only ever consulted to look up
 * a value in the closed `CURATED_MFA_ERRORS` allowlist, so an unrelated `err.code`
 * (e.g. a native `'ECONNREFUSED'`) is harmless — it simply won't match.
 *
 * @param {unknown} err
 * @returns {string|null}
 */
/**
 * The server's machine code, or null. ONE spelling — `err.errorCode` (AUTH-ERROR-VOCABULARY-001):
 * the four spellings this used to read (`data.code`, `data.error.code`, `response.data.error.code`,
 * top-level `code`) were four contracts for one fact. `code` in particular is the TRANSPORT
 * code on the fleet's errors (`'NETWORK_ERROR'`, axios `ERR_*`), never a server code.
 * @param {unknown} err
 * @returns {string|null}
 */
function extractBackendCode(err) {
  if (err && typeof err === 'object' && typeof err.errorCode === 'string') return err.errorCode;
  return null;
}

/**
 * Translate a raw backend/transport error into an `AuthError` suitable for
 * surfacing to end-users. The original is preserved via `cause`.
 *
 * @param {unknown} err                    Raw error thrown by auth transport layer.
 * @param {'login'|'mfa'} [operation='login']  Which flow the error arose from.
 * @returns {AuthError}
 */
export function translateAuthError(err, operation = 'login') {
  // W19 (LOGIN-CONVERGENCE): an error this translator already made is returned AS IS. A host that
  // translates before an adapter translates again would otherwise see its AuthError — which carries
  // `code`, never `status` — reclassified as a network failure.
  if (err instanceof AuthError) return err;
  const status = extractStatus(err);

  // MFA-LOGIN-CONSOLIDATION-001: on the MFA step, a recognized curated verify code
  // wins over the generic — distinguishing retryable from terminal. The password
  // step is untouched (gated on operation). Off-allowlist codes fall through to the
  // generic, anti-enumeration message below.
  if (operation === 'mfa') {
    const backendCode = extractBackendCode(err);
    const curated = CURATED_MFA_ERRORS[backendCode];
    if (curated) {
      return translated(curated.key, err, {
        errorClass: 'mfa',
        code: backendCode,
        terminal: curated.terminal,
      });
    }
  }

  // Any step (sign-in, MFA, a session restore): the person is who they said; only their stores could not be read.
  if (extractBackendCode(err) === LOCATIONS_UNAVAILABLE_CODE) {
    return translated('login.error.locationsUnavailable', err, {
      errorClass: 'server',
      code: LOCATIONS_UNAVAILABLE_CODE,
      terminal: false,
    });
  }

  if (operation === 'login' && extractBackendCode(err) === MFA_SETUP_REQUIRED_CODE) {
    return translated('login.error.mfaEnrollmentRequired', err, {
      errorClass: 'mfa_enrollment',
      code: MFA_SETUP_REQUIRED_CODE,
    });
  }

  const heldFactor = operation === 'login' ? factorOfSetupRequired(extractBackendCode(err)) : null;
  if (heldFactor && Object.hasOwn(FACTOR_HOLD_KEYS, heldFactor)) {
    return translated(FACTOR_HOLD_KEYS[heldFactor], err, {
      errorClass: 'factor_enrollment',
      code: FACTOR_SETUP_REQUIRED[heldFactor],
    });
  }

  if (extractBackendCode(err) === DEVICE_LOGIN_NOT_PERMITTED_CODE) {
    return translated('login.error.deviceLoginNotPermitted', err, {
      errorClass: 'device_login',
      code: DEVICE_LOGIN_NOT_PERMITTED_CODE,
      terminal: true,
    });
  }

  if (status === 401 || status === 403 || status === 404) {
    const messageKey = operation === 'mfa' ? 'login.error.mfa' : 'login.error.credentials';
    return translated(messageKey, err, { errorClass: 'credentials', code: status });
  }

  if (status === 429) {
    return translated('login.error.rateLimit', err, { errorClass: 'rate_limit', code: status });
  }

  if (status !== null && status >= 500 && status <= 599) {
    return translated('login.error.server', err, { errorClass: 'server', code: status });
  }

  // AUTH-LOGIN-FIDELITY-001: ANY remaining client error (400-499 not already
  // classified as credentials/rate-limit above — e.g. 400, 409 Conflict, 422
  // Unprocessable Entity, 423 Locked) is a definitive server RESPONSE, not a
  // transport failure. It must NOT fall through to "Network error." (the original
  // bug — generalized beyond 400 to the whole client-error range). A neutral
  // message is correct here (a 4xx is not a credentials oracle).
  if (status !== null && status >= 400 && status <= 499) {
    return translated('login.error.badRequest', err, { errorClass: 'bad_request', code: status });
  }

  // `network` is reserved for genuinely status-less errors: a missing status
  // (fetch threw before any response) or `0` (the transport's network-fail
  // sentinel). Any non-null, non-4xx/5xx status (e.g. a stray 3xx/1xx) also
  // lands here as a transport anomaly. The original status is forwarded on
  // `code` so callers can still distinguish the cause.
  return translated('login.error.network', err, { errorClass: 'network', code: status });
}
